import { EdgeData, NodeData } from 'reaflow';
import { useSystemOverview } from '../api/getSystemOverview';
import { SystemOverviewDto } from '../types';

export const useSystemOverviewGraph = () => {
  const { data: systemOverview } = useSystemOverview({});
  const nodes: NodeData<any>[] = [];
  const edges: EdgeData<any>[] = [];
  // Add satellites and their linked items
  if (systemOverview?.satellites) {
    for (const satelliteId of Object.keys(systemOverview?.satellites)) {
      addSatelliteNode(nodes, satelliteId, systemOverview);
      for (const dishId of systemOverview.satellites[Number(satelliteId)]
        .dishIds) {
        addAndLinkDishNode(nodes, edges, dishId, satelliteId, systemOverview);
        for (const switchId of systemOverview.dishes[Number(dishId)]
          .switchIds) {
          addAndLinkSwitchAndAnalyzers(
            nodes,
            edges,
            dishId,
            switchId,
            systemOverview
          );
        }
        addAndLinkAnalyzers(nodes, edges, dishId, systemOverview);
      }
    }
  }
  // Add dishes not linked to satellites
  if (systemOverview?.dishes) {
    for (const dishId of Object.keys(systemOverview.dishes)) {
      addDishNode(nodes, dishId, systemOverview);
      for (const switchId of systemOverview.dishes[Number(dishId)].switchIds) {
        addAndLinkSwitchAndAnalyzers(
          nodes,
          edges,
          dishId,
          switchId,
          systemOverview
        );
      }
      addAndLinkAnalyzers(nodes, edges, dishId, systemOverview);
    }
  }
  // Add switches and analyzers not linked to dishes
  if (systemOverview?.switches) {
    for (const switchId of Object.keys(systemOverview.switches)) {
      addSwitchAndAnalyzers(nodes, edges, switchId, systemOverview);
    }
  }
  if (systemOverview?.analyzers) {
    for (const analyzerId of Object.keys(systemOverview.analyzers)) {
      addAnalyzers(nodes, edges, analyzerId, systemOverview);
    }
  }
  // Add and link locations
  if (systemOverview?.locations) {
    for (const locationId of Object.keys(systemOverview.locations)) {
      addLocation(nodes, locationId, systemOverview);
      for (const dishId of systemOverview.locations[Number(locationId)]
        .dishIds) {
        linkDishNodeToLocation(edges, dishId, locationId);
      }
    }
  }
  return { nodes, edges };
};

const addSatelliteNode = (
  nodes: NodeData<any>[],
  satelliteId: string,
  systemOverview: SystemOverviewDto
) => {
  nodes.push({
    id: 'satellite-' + satelliteId,
    text: systemOverview.satellites[Number(satelliteId)].name,
    className: 'satelliteNode',
  });
};

const addDishNode = (
  nodes: NodeData<any>[],
  dishId: string,
  systemOverview: SystemOverviewDto
) => {
  if (!nodes.some((node) => node.id === 'dish-' + dishId))
    nodes.push({
      id: 'dish-' + dishId,
      text: systemOverview.dishes[Number(dishId)].name,
      className: 'dishNode',
    });
};

const addAndLinkDishNode = (
  nodes: NodeData<any>[],
  edges: EdgeData<any>[],
  dishId: string | number,
  satelliteId: string,
  systemOverview: SystemOverviewDto
) => {
  addDishNode(nodes, dishId as string, systemOverview);
  edges.push({
    id: 'satelliteDish-' + satelliteId + '-' + dishId,
    from: 'satellite-' + satelliteId,
    to: 'dish-' + dishId,
  });
};

const linkDishNodeToLocation = (
  edges: EdgeData<any>[],
  dishId: string | number,
  locationId: string | number
) => {
  edges.push({
    id: 'locationDish-' + locationId + '-' + dishId,
    from: 'location-' + locationId,
    to: 'dish-' + dishId,
  });
};

const addAndLinkSwitchAndAnalyzers = (
  nodes: NodeData<any>[],
  edges: EdgeData<any>[],
  dishId: number | string,
  switchId: number | string,
  systemOverview: SystemOverviewDto
) => {
  if (!nodes.some((node) => node.id === 'switch-' + switchId))
    nodes.push({
      id: 'switch-' + switchId,
      text: systemOverview.switches[Number(switchId)].name,
      className: systemOverview.switches[Number(switchId)].enabled
        ? 'switchNode'
        : 'switchNodeDisabled',
    });
  if (
    !edges.some((edge) => edge.id === 'dishSwitch-' + dishId + '-' + switchId)
  )
    edges.push({
      id: 'dishSwitch-' + dishId + '-' + switchId,
      from: 'dish-' + dishId,
      to: 'switch-' + switchId,
    });
  for (const analyzerId of systemOverview.switches[Number(switchId)]
    .analyzerIds) {
    if (!nodes.some((node) => node.id === 'analyzer-' + analyzerId))
      nodes.push({
        id: 'analyzer-' + analyzerId,
        text: systemOverview.analyzers[Number(analyzerId)].name,
        className: 'analyzerNode',
      });
    if (
      !edges.some(
        (edge) => edge.id === 'switchAnalyzer-' + switchId + '-' + analyzerId
      )
    )
      edges.push({
        id: 'switchAnalyzer-' + switchId + '-' + analyzerId,
        from: 'switch-' + switchId,
        to: 'analyzer-' + analyzerId,
      });
  }
};

const addSwitchAndAnalyzers = (
  nodes: NodeData<any>[],
  edges: EdgeData<any>[],
  switchId: number | string,
  systemOverview: SystemOverviewDto
) => {
  if (!nodes.some((node) => node.id === 'switch-' + switchId))
    nodes.push({
      id: 'switch-' + switchId,
      text: systemOverview.switches[Number(switchId)].name,
      className: systemOverview.switches[Number(switchId)].enabled
        ? 'switchNode'
        : 'switchNodeDisabled',
    });
  for (const analyzerId of systemOverview.switches[Number(switchId)]
    .analyzerIds) {
    if (!nodes.some((node) => node.id === 'analyzer-' + analyzerId))
      nodes.push({
        id: 'analyzer-' + analyzerId,
        text: systemOverview.analyzers[Number(analyzerId)].name,
        className: 'analyzerNode',
      });
    if (
      !edges.some(
        (edge) => edge.id === 'switchAnalyzer-' + switchId + '-' + analyzerId
      )
    )
      edges.push({
        id: 'switchAnalyzer-' + switchId + '-' + analyzerId,
        from: 'switch-' + switchId,
        to: 'analyzer-' + analyzerId,
      });
  }
};

const addAndLinkAnalyzers = (
  nodes: NodeData<any>[],
  edges: EdgeData<any>[],
  dishId: number | string,
  systemOverview: SystemOverviewDto
) => {
  for (const analyzerId of systemOverview.dishes[Number(dishId)].analyzerIds) {
    if (!nodes.some((node) => node.id === 'analyzer-' + analyzerId))
      nodes.push({
        id: 'analyzer-' + analyzerId,
        text: systemOverview.analyzers[Number(analyzerId)].name,
        className: 'analyzerNode',
      });
    if (
      !edges.some(
        (edge) => edge.id === 'dishAnalyzer-' + dishId + '-' + analyzerId
      )
    )
      edges.push({
        id: 'dishAnalyzer-' + dishId + '-' + analyzerId,
        from: 'dish-' + dishId,
        to: 'analyzer-' + analyzerId,
      });
  }
};

const addAnalyzers = (
  nodes: NodeData<any>[],
  edges: EdgeData<any>[],
  analyzerId: number | string,
  systemOverview: SystemOverviewDto
) => {
  if (!nodes.some((node) => node.id === 'analyzer-' + analyzerId))
    nodes.push({
      id: 'analyzer-' + analyzerId,
      text: systemOverview.analyzers[Number(analyzerId)].name,
      className: 'analyzerNode',
    });
};

const addLocation = (
  nodes: NodeData<any>[],
  locationId: string | number,
  systemOverview: SystemOverviewDto
) => {
  nodes.push({
    id: 'location-' + locationId,
    text: systemOverview.locations[Number(locationId)].name,
    className: 'locationNode',
  });
};
